import { ratingModel } from './ratingModel';
import { leaderboardModel } from './leaderboardModel';
import { ensureClient } from './util';
import { Tables } from '@/types/supabase';
import { authModel } from './authModel';
import { getSupabaseAdminClient } from '@/lib/supabase/admin';
import type { MatchWithRating } from '@/components/match/types';

const TABLE_NAME = 'player';

// Types
export type Gender = 'male' | 'female';
type Player = Tables<'player'>;

// Extended player type that includes secret data
export type PlayerWithSecret = Player & {
  date_of_birth?: string | null;
  email?: string | null;
};

async function get(playerId: string): Promise<Player | null> {
  const client = ensureClient();
  const { data, error } = await client
    .from(TABLE_NAME)
    .select('*')
    .eq('id', playerId)
    .maybeSingle();
  if (error) throw error;
  return data as Player | null;
}

async function getCurrentUserPlayer(): Promise<Player | null> {
  const client = ensureClient();
  const session = await authModel.getSession();
  if (!session) return null;
  const userId = session.user.id;
  if (!userId) return null;

  const { data, error } = await client
    .from(TABLE_NAME)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;

  return data as Player | null;
}

async function getByUsername(username: string): Promise<Player | null> {
  const client = ensureClient();
  const { data, error } = await client
    .from(TABLE_NAME)
    .select('*')
    .eq('username', username)
    .maybeSingle();

  if (error) throw error;
  return data as Player | null;
}

async function getByUserId(userId: string): Promise<Player | null> {
  const client = ensureClient();

  const { data, error } = await client
    .from(TABLE_NAME)
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;

  return data as Player | null;
}

async function getGenderByUserId(userId: string): Promise<Gender | null> {
  const client = ensureClient();

  const { data, error } = await client
    .from(TABLE_NAME)
    .select('gender')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error(`Error getting ${TABLE_NAME} gender:`, error);
    throw error;
  }

  const gender = data?.gender as string | null;

  if (
    gender === 'male' ||
    gender === 'female' ||
    gender === 'other' ||
    gender === 'prefer_not_to_say'
  ) {
    return gender as Gender;
  }

  return null;
}

async function getIdByUserId(userId: string): Promise<string | null> {
  const client = ensureClient();

  const { data, error } = await client
    .from(TABLE_NAME)
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error(`Error getting ${TABLE_NAME} id:`, error);
    throw error;
  }

  return data?.id || null;
}

async function getByUserIds(userIds: string[]): Promise<Player[]> {
  const client = ensureClient();

  const { data, error } = await client.from(TABLE_NAME).select('*').in('user_id', userIds);

  if (error) {
    console.error(`Error getting ${TABLE_NAME} by user ids:`, error);
    throw error;
  }

  return (data || []) as Player[];
}

async function getByIds(playerIds: string[]): Promise<Player[]> {
  const client = ensureClient();

  const { data, error } = await client.from(TABLE_NAME).select('*').in('id', playerIds);

  if (error) {
    console.error(`Error getting ${TABLE_NAME} by ids:`, error);
    throw error;
  }

  return (data || []) as Player[];
}

function isWin(result: string): boolean {
  const scoreParts = result.split('-');
  if (scoreParts.length === 2) {
    const myGames = parseInt(scoreParts[0], 10);
    const oppGames = parseInt(scoreParts[1], 10);
    return !isNaN(myGames) && !isNaN(oppGames) && myGames > oppGames;
  }
  return result.includes('W') || result.includes('won');
}

async function getPlayerStats(username: string): Promise<{
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
  currentRank: number | null;
  federationName: string | null;
  recentMatches: Array<{
    id: string;
    opponent: string;
    opponentUsername: string;
    result: string;
    dateISO: string;
  }>;
}> {
  try {
    // Get match history
    const matchHistory = await ratingModel.getMatchHistoryForPlayer({ slug: username });

    // Filter out rating adjustments (matches with isAdjustment = true)
    const actualMatches = matchHistory.filter((match) => !match.isAdjustment);

    // Calculate statistics
    const totalMatches = actualMatches.length;
    const wins = actualMatches.filter((m) => isWin(m.result)).length;
    const losses = totalMatches - wins;
    const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

    // Try to get ranking position
    let currentRank: number | null = null;
    let federationName: string | null = null;

    try {
      const federations = await leaderboardModel.getFederationsForCurrentUser();
      if (federations.length > 0) {
        const federation = federations[0]; // Use first federation
        federationName = federation.name;
        const leaderboard = await leaderboardModel.getRowsForFederation(federation.id);

        // Get player record to find player ID
        const player = await getByUsername(username);
        if (player) {
          const myIndex = leaderboard.findIndex((row) => row.playerId === player.id);
          currentRank = myIndex >= 0 ? myIndex + 1 : null;
        }
      }
    } catch (rankError) {
      console.log('Could not fetch ranking:', rankError);
    }

    return {
      totalMatches,
      wins,
      losses,
      winRate,
      currentRank,
      federationName,
      recentMatches: actualMatches.slice(0, 3).map((m) => ({
        id: m.id,
        opponent: m.opponent,
        opponentUsername: m.opponentUsername,
        result: m.result,
        dateISO: m.dateISO,
      })),
    };
  } catch (error) {
    console.error('Failed to get player stats:', error);
    return {
      totalMatches: 0,
      wins: 0,
      losses: 0,
      winRate: 0,
      currentRank: null,
      federationName: null,
      recentMatches: [],
    };
  }
}

export type PlayerProfileData = {
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
  currentRank: number | null;
  leaderboardSize: number | null;
  federationName: string | null;
  matchHistory: MatchWithRating[];
};

async function getPlayerProfileData(
  username: string,
  options?: { isJapanese?: boolean },
): Promise<PlayerProfileData> {
  try {
    const matchHistory = await ratingModel.getMatchHistoryForPlayer({
      slug: username,
      isJapanese: options?.isJapanese,
    });
    const actualMatches = matchHistory.filter((match) => !match.isAdjustment);

    const totalMatches = actualMatches.length;
    const wins = actualMatches.filter((m) => isWin(m.result)).length;
    const losses = totalMatches - wins;
    const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

    let currentRank: number | null = null;
    let leaderboardSize: number | null = null;
    let federationName: string | null = null;

    try {
      const federations = await leaderboardModel.getFederationsForCurrentUser();
      if (federations.length > 0) {
        const federation = federations[0];
        federationName = federation.name;
        const leaderboard = await leaderboardModel.getRowsForFederation(federation.id);
        leaderboardSize = leaderboard.length;

        const player = await getByUsername(username);
        if (player) {
          const myIndex = leaderboard.findIndex((row) => row.playerId === player.id);
          currentRank = myIndex >= 0 ? myIndex + 1 : null;
        }
      }
    } catch (rankError) {
      console.log('Could not fetch ranking:', rankError);
    }

    return {
      totalMatches,
      wins,
      losses,
      winRate,
      currentRank,
      leaderboardSize,
      federationName,
      matchHistory,
    };
  } catch (error) {
    console.error('Failed to get player profile data:', error);
    return {
      totalMatches: 0,
      wins: 0,
      losses: 0,
      winRate: 0,
      currentRank: null,
      leaderboardSize: null,
      federationName: null,
      matchHistory: [],
    };
  }
}

async function update(playerId: string, payload: Partial<Player>) {
  const client = ensureClient();
  const { error } = await client.from(TABLE_NAME).update(payload).eq('id', playerId);
  if (error) throw error;
}

async function adminUpdate(playerId: string, payload: Partial<Player>) {
  const client = getSupabaseAdminClient();
  if (!client) throw new Error('Supabase admin not configured');
  const { error } = await client.from(TABLE_NAME).update(payload).eq('id', playerId);
  if (error) throw error;
}

// sync rating to latest rating in rating table
async function adminSyncRating(playerId: string) {
  const client = getSupabaseAdminClient();
  if (!client) throw new Error('Supabase admin not configured');
  const { data, error } = await client
    .from('rating')
    .select('rating_after')
    .eq('player_id', playerId)
    // History-only rows (null K-factor, event had ratings off) are not a rating.
    .not('k_factor', 'is', null)
    .order('rated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;

  const player = await get(playerId);
  // When no rating rows remain, fall back to the player's starting rating
  // rather than 0 (a deleted/converted match may have been their only match).
  const latestRating = data?.rating_after ?? player?.initial_rating ?? 1500;

  if (latestRating !== player?.current_rating) {
    await adminUpdate(playerId, { current_rating: latestRating });
  }
}

async function adminGetUserIdById(playerId: string): Promise<string | null> {
  const client = getSupabaseAdminClient();
  if (!client) throw new Error('Supabase admin not configured');
  const { data, error } = await client
    .from(TABLE_NAME)
    .select('user_id')
    .eq('id', playerId)
    .maybeSingle();
  if (error) throw error;
  return data?.user_id || null;
}

// Get player's federation membership
async function getPlayerFederation(playerId: string): Promise<string | null> {
  const client = ensureClient();
  const { data, error } = await client
    .from('federation_user')
    .select('federation_id')
    .eq('player_id', playerId)
    .eq('status', 'active')
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.federation_id || null;
}

// Get player's primary location
async function getPlayerLocation(playerId: string): Promise<string | null> {
  const client = ensureClient();
  const { data, error } = await client
    .from('player_location')
    .select('location_id')
    .eq('player_id', playerId)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.location_id || null;
}

// Get player's club location
async function getPlayerClub(playerId: string): Promise<string | null> {
  const client = ensureClient();
  const { data, error } = await client
    .from('player_location')
    .select(
      `
      location_id,
      location:location_id (
        type
      )
    `,
    )
    .eq('player_id', playerId)
    .eq('status', 'active')
    .eq('location.type', 'club')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data?.location_id || null;
}

export {
  get,
  getCurrentUserPlayer,
  getByUsername,
  getByUserId,
  getPlayerStats,
  getPlayerProfileData,
  getGenderByUserId,
  getIdByUserId,
  getByUserIds,
  getByIds,
  update,
  adminUpdate,
  adminSyncRating,
  adminGetUserIdById,
  getPlayerFederation,
  getPlayerLocation,
  getPlayerClub,
};
